/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import type { SeriesRecordingOptionsOverlayProps } from "./SeriesRecordingOptionsOverlayProps";
import "./SeriesRecordingOptionsOverlay.scss";
import {
  RecordingOption,
  SAVE_ACTION_INDEX,
  CANCEL_ACTION_INDEX
} from "../../../viewModel/myHub/seriesRecordingOptions";
import { ActionButton } from "../../common";
import { RecordingOptionRow } from "./RecordingOptionRow";

/**
 * SeriesRecordingOptionsOverlay — presentational overlay for the "Series
 * Recording Options" screen (Figma: My Hub Detail, node 191:1623).
 *
 * Composes {@link RecordingOptionRow} for each option and the shared
 * {@link ActionButton} for Save/Cancel. It holds no state: the focused control
 * is supplied via the `focus` prop and D-pad input is handled by
 * `SeriesRecordingOptionsOverlayController` on the modal stack.
 */
const SeriesRecordingOptionsOverlay: FunctionComponent<
  SeriesRecordingOptionsOverlayProps
> = (props: SeriesRecordingOptionsOverlayProps): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, sonarjs/no-unused-vars, sonarjs/no-dead-store -- Required by CodingGuidelines §7 (D-09)
  const TAG: string = "SeriesRecordingOptionsOverlay";
  const {
    title,
    options,
    saveButtonLabel,
    cancelButtonLabel,
    focus,
    onOptionActivate,
    onOptionFocus,
    onSave,
    onCancel,
    onActionFocus
  } = props;

  const isActionFocused = (index: number): boolean =>
    focus.zone === "actions" && focus.index === index;

  return (
    <div className="series-recording-options">
      <div
        className="series-recording-options__dimmer"
        aria-hidden="true"
        onClick={onCancel}
      />
      <section
        className="series-recording-options__panel"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <h2 className="series-recording-options__title">{title}</h2>

        <ul className="series-recording-options__list">
          {options.map(
            (option: RecordingOption, index: number): JSX.Element => (
              <li
                key={option.optionListType}
                className="series-recording-options__list-item"
                onMouseEnter={(): void => onOptionFocus(option.optionListType)}
                onFocus={(): void => onOptionFocus(option.optionListType)}
              >
                <RecordingOptionRow
                  optionListType={option.optionListType}
                  label={option.label}
                  description={option.description}
                  value={option.value}
                  isFocused={focus.zone === "rows" && focus.index === index}
                  onSelect={onOptionActivate}
                />
              </li>
            )
          )}
        </ul>

        <div className="series-recording-options__actions">
          <div
            onMouseEnter={(): void => onActionFocus(SAVE_ACTION_INDEX)}
            onFocus={(): void => onActionFocus(SAVE_ACTION_INDEX)}
          >
            <ActionButton
              label={saveButtonLabel}
              onClick={onSave}
              isFocused={isActionFocused(SAVE_ACTION_INDEX)}
              className="series-recording-options__action-button"
            />
          </div>
          <div
            onMouseEnter={(): void => onActionFocus(CANCEL_ACTION_INDEX)}
            onFocus={(): void => onActionFocus(CANCEL_ACTION_INDEX)}
          >
            <ActionButton
              label={cancelButtonLabel}
              onClick={onCancel}
              isFocused={isActionFocused(CANCEL_ACTION_INDEX)}
              className="series-recording-options__action-button"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default SeriesRecordingOptionsOverlay;
